// Survivor equipment helpers. Survivor power comes from equippable items
// instead of bought permanent stat floors.

import { getItem } from "./items.mjs";

export const MAX_EQUIPPED_ITEMS = 3;

export const EQUIPMENT_LOADOUT_SLOTS = {
  weapon: "weapon",
  defense: "defense",
  utility: "utility",
};

export const EQUIPMENT_LOADOUT_SLOT_ORDER = [
  EQUIPMENT_LOADOUT_SLOTS.weapon,
  EQUIPMENT_LOADOUT_SLOTS.defense,
  EQUIPMENT_LOADOUT_SLOTS.utility,
];

export const EQUIPMENT_STATS = {
  offense: "offense",
  defense: "defense",
  moveSpeed: "moveSpeed",
  carryCapacity: "carryCapacity",
};

export function equipmentLoadoutSlot(itemId) {
  const item = getItem(itemId);
  const equipment = item?.equipment;
  if (!equipment) return null;
  if (equipment.loadoutSlot) return equipment.loadoutSlot;
  if (equipment.slot === "weapon") return EQUIPMENT_LOADOUT_SLOTS.weapon;
  if (equipment.effects?.moveBonusMetersPerTurn || equipment.effects?.carryCapacityBonus || equipment.effects?.scavengeTurnsDelta) {
    return EQUIPMENT_LOADOUT_SLOTS.utility;
  }
  if (equipment.effects?.defenseBonus) return EQUIPMENT_LOADOUT_SLOTS.defense;
  if (equipment.effects?.offenseBonus) return EQUIPMENT_LOADOUT_SLOTS.weapon;
  return EQUIPMENT_LOADOUT_SLOTS.utility;
}

export function normalizeEquipmentItemIds(itemIds) {
  const usedSlots = new Set();
  const normalized = [];
  for (const itemId of Array.isArray(itemIds) ? itemIds : []) {
    if (!getItem(itemId)?.equipment) continue;
    const slot = equipmentLoadoutSlot(itemId);
    if (!slot || usedSlots.has(slot)) continue;
    usedSlots.add(slot);
    normalized.push(itemId);
    if (normalized.length >= MAX_EQUIPPED_ITEMS) break;
  }
  return normalized;
}

export function equipmentSlotsUsed(survivor) {
  return normalizeEquipmentItemIds(survivor?.equipmentItemIds).length;
}

export function equipmentSlotsAvailable(survivor) {
  return MAX_EQUIPPED_ITEMS - equipmentSlotsUsed(survivor);
}

export function equipmentLoadoutMap(itemIds) {
  const entries = new Map();
  normalizeEquipmentItemIds(itemIds).forEach((itemId, index) => {
    entries.set(equipmentLoadoutSlot(itemId), { itemId, index });
  });
  return entries;
}

export function canEquipItemId(equippedItemIds, itemId) {
  const slot = equipmentLoadoutSlot(itemId);
  if (!slot) return false;
  return !equipmentLoadoutMap(equippedItemIds).has(slot);
}

export function getEquipmentEffects(itemIds) {
  const effects = {
    offenseBonus: 0,
    defenseBonus: 0,
    moveBonusMetersPerTurn: 0,
    carryCapacityBonus: 0,
    scavengeTurnsDelta: 0,
  };
  for (const itemId of normalizeEquipmentItemIds(itemIds)) {
    const item = getItem(itemId);
    const itemEffects = item?.equipment?.effects;
    if (!itemEffects) continue;
    effects.offenseBonus += itemEffects.offenseBonus ?? 0;
    effects.defenseBonus += itemEffects.defenseBonus ?? 0;
    effects.moveBonusMetersPerTurn += itemEffects.moveBonusMetersPerTurn ?? 0;
    effects.carryCapacityBonus += itemEffects.carryCapacityBonus ?? 0;
    effects.scavengeTurnsDelta += itemEffects.scavengeTurnsDelta ?? 0;
  }
  return effects;
}
